import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePoints } from '../hooks/usePoints';
import { Notify } from '../components/Notify';

interface CartContextProps {
  addProductToCart: (product: any) => void;
  removeProductToCart: (productId: string) => void;
  decrementProductToCart: (product: any) => void;
  clearCart: () => void;
  cart: Array<any>;
  isAwardMoney: boolean;
  isAwardVariable: boolean;
  totalPremiumPrice: number;
  totalPaymentValue: number;
}

interface CartContextProviderProps {
  children: ReactNode;
}

export const cartContext = createContext({} as CartContextProps);

export function CartContextProvider(props: CartContextProviderProps) {
  const { player, getClientByAliasAndPlayer } = useAuth();
  const { setRemainingPoints, remainingPoints, availableToRedeem } =
    usePoints();
  let balance = 0;
  if (player) {
    const { player: playerData } = player;
    balance = playerData
      ? Number(playerData.totalPoints) -
        Number(playerData.redeemedPoints ? playerData.redeemedPoints : 0)
      : 0;
  }
  const [cart, setCart] = useState<Array<any>>([]);
  const [isAwardMoney, setIsAwardMoney] = useState(false);
  const [totalPremiumPrice, setTotalPremiumPrice] = useState(0);
  const [totalPaymentValue, setTotalPaymentValue] = useState(0);
  const [isAwardVariable, setIsAwardVariable] = useState(false);

  useEffect(() => {
    if (!cart || cart.length === 0) {
      const sessionCart = sessionStorage.getItem('cart');
      if (sessionCart) {
        sessionCart && setCart(JSON.parse(sessionCart));

        const sessionRemainingPoints =
          sessionStorage.getItem('remainingPoints');

        if (sessionRemainingPoints) {
          const sessionRemainingPointsNumber = JSON.parse(
            sessionRemainingPoints
          );
          sessionRemainingPointsNumber &&
            setRemainingPoints(sessionRemainingPointsNumber);
        }
      }
    }
  }, [
    sessionStorage.getItem('cart'),
    sessionStorage.getItem('remainingPoints'),
  ]);

  useEffect(() => {
    const isMoney = cart.some((item) => item.premiumType.value === 'BS2');
    setIsAwardMoney(isMoney);

    const totalPremiumPrice = cart.reduce((sum, it) => {
      return sum + it.premiumPrice * it.amount;
    }, 0);
    setTotalPremiumPrice(totalPremiumPrice);

    const totalPaymentValue = cart.reduce((sum, it) => {
      const paymentValue = it.paymentValue ? Number(it.paymentValue) : 0;
      return sum + paymentValue * it.amount;
    }, 0);
    setTotalPaymentValue(totalPaymentValue);

    const isAwardVariable = cart.some(
      (item) => item.valueType && item.valueType.value === 'variable'
    );
    setIsAwardVariable(isAwardVariable);
  }, [cart]);

  const recalculatePoints = (product: any, type: string) => {
    if (type === 'increment') {
      // lógica para recalcular os pontos restantes
      if (product.valueType && product.valueType.value === 'variable') {
        const newRemainingPoints =
          Number(remainingPoints) - Number(availableToRedeem);
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      } else {
        const newRemainingPoints =
          Number(remainingPoints) - Number(product.premiumPrice);
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      }
    } else {
      // lógica para recalcular os pontos restantes
      if (product.valueType && product.valueType.value === 'variable') {
        const newRemainingPoints =
          Number(remainingPoints) + Number(availableToRedeem);
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      } else {
        const newRemainingPoints =
          Number(remainingPoints) + Number(product.premiumPrice);
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      }
    }
  };

  const getIndexProductById = (premiumId: string) => {
    return cart.findIndex((product) => product.premiumId === premiumId);
  };

  const addProductToCart = (product: any): void => {
    if (
      cart.reduce((sum, it) => {
        return sum + it.premiumPrice * it.amount;
      }, 0) >= 15000
    ) {
      Notify(
        'O valor máximo permitido para resgate por dia é de R$15.000',
        'warn'
      );
      return;
    }
    const productIndex = getIndexProductById(product.premiumId);

    // lógica para recalcular os pontos restantes
    recalculatePoints(product, 'increment');

    if (productIndex >= 0) {
      const newCart = [...cart];
      newCart[productIndex].amount = newCart[productIndex].amount + 1;

      newCart[productIndex].subtotal = newCart[productIndex].paymentValue
        ? Number(newCart[productIndex].paymentValue) *
          newCart[productIndex].amount
        : newCart[productIndex].premiumPrice * newCart[productIndex].amount;

      setCart(newCart);
      sessionStorage.setItem('cart', JSON.stringify(newCart));
    } else {
      const newProduct = {
        ...product,
        amount: 1,
        subtotal: product.paymentValue
          ? product.paymentValue
          : product.premiumPrice,
      };
      setCart([...cart, newProduct]);
      const sessionCart = [...cart, newProduct];
      sessionStorage.setItem('cart', JSON.stringify(sessionCart));
    }
  };

  const decrementProductToCart = (product: any) => {
    const productIndex = getIndexProductById(product.premiumId);

    if (productIndex >= 0) {
      const newCart = [...cart];

      if (newCart[productIndex].amount === 1) return;
      // lógica para recalcular os pontos restantes
      recalculatePoints(product, 'decrement');
      newCart[productIndex].amount = newCart[productIndex].amount - 1;

      newCart[productIndex].subtotal = newCart[productIndex].paymentValue
        ? Number(newCart[productIndex].paymentValue) *
          newCart[productIndex].amount
        : newCart[productIndex].premiumPrice * newCart[productIndex].amount;

      setCart(newCart);
      sessionStorage.setItem('cart', JSON.stringify(newCart));
    }
  };

  const removeProductToCart = (productId: string) => {
    const productIndex = getIndexProductById(productId);
    if (productIndex >= 0) {
      const newCart = [...cart];

      // lógica para recalcular os pontos restantes
      if (
        newCart[productIndex].valueType &&
        newCart[productIndex].valueType.value === 'variable'
      ) {
        const newRemainingPoints =
          Number(remainingPoints) + Number(availableToRedeem)
            ? Number(availableToRedeem)
            : Number(balance);
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      } else {
        const removePoints =
          newCart[productIndex].amount * newCart[productIndex].premiumPrice;

        const newRemainingPoints = Number(remainingPoints) + removePoints;
        setRemainingPoints(newRemainingPoints);

        sessionStorage.setItem(
          'remainingPoints',
          JSON.stringify(newRemainingPoints)
        );
      }

      const newProducts = cart.filter((p) => p.premiumId !== productId);

      setCart(newProducts);
      sessionStorage.setItem('cart', JSON.stringify(newProducts));
    }
  };

  const clearCart = async () => {
    setCart([]);
    await getClientByAliasAndPlayer();
  };

  return (
    <cartContext.Provider
      value={{
        addProductToCart,
        removeProductToCart,
        decrementProductToCart,
        clearCart,
        cart,
        isAwardMoney,
        isAwardVariable,
        totalPaymentValue,
        totalPremiumPrice,
      }}
    >
      {props.children}
    </cartContext.Provider>
  );
}
